'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useLogger } from 'next-axiom';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { cn } from '../../../../lib/utils';
import { useWishlistMutations } from '../../../../wishlist/client/hooks/use-wishlist-mutations';
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from '../../../responsive-dialog';
import { Button } from '../../../ui/button';
import { DialogFooter } from '../../../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form';
import { Input } from '../../../ui/input';

const createBoardSchema = z.object({
  name: z
    .string()
    .min(1, 'Board name is required')
    .max(50, 'Board name must be 50 characters or less')
    .trim(),
});

type CreateBoardFormData = z.infer<typeof createBoardSchema>;

interface CreateBoardDialogProps {
  children: ReactNode;
  onSuccess?: (boardId: string) => void;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
}

export function CreateBoardDialog({
  children,
  onSuccess,
  primaryButtonClassName,
  secondaryButtonClassName,
}: CreateBoardDialogProps) {
  const [open, setOpen] = useState(false);
  const { createListMutation } = useWishlistMutations();
  const logger = useLogger();

  const form = useForm<CreateBoardFormData>({
    resolver: zodResolver(createBoardSchema),
    defaultValues: {
      name: '',
    },
  });

  const onSubmit = async (data: CreateBoardFormData) => {
    try {
      const result = await createListMutation.mutateAsync(data.name);

      if (result?.id) {
        onSuccess?.(result.id);
        setOpen(false);
        form.reset();
      }
    } catch (error) {
      logger.error('Failed to create board', { error });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      form.reset();
    }
  };

  return (
    <ResponsiveDialog isOpen={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogTrigger asChild>{children}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        title="Create New Board"
        description="Create a new board to organize your wishlist items"
        className="max-w-md"
      >
        <div className="pt-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-4 px-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(var(--color))]">
                        Board Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Kitchen Renovation, Dream Bathroom..."
                          {...field}
                          disabled={createListMutation.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter className="flex gap-2">
                <ResponsiveDialogClose asChild>
                  <Button
                    type="button"
                    className={secondaryButtonClassName}
                    disabled={createListMutation.isPending}
                  >
                    Cancel
                  </Button>
                </ResponsiveDialogClose>
                <Button
                  type="submit"
                  disabled={createListMutation.isPending}
                  className={cn('min-w-20', primaryButtonClassName)}
                >
                  {createListMutation.isPending ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Creating...
                    </>
                  ) : (
                    'Create Board'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
